// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ProductImagesBadges from "@eisinc/App/Components/CustomBadges/ProductImagesBadges";
import VariantParentProductPrice from "@eisinc/App/Components/Price/VariantParentProductPrice";
import ProductActions, { ProductActionsStyle } from "@eisinc/App/Components/Product/ProductActions";
import ProductGridContainer, {
    ProductGridContainerStyle,
} from "@eisinc/App/Components/ProductGridCard/ProductGridContainer";
import Skeleton from "@eisinc/App/Components/Skeleton/Skeleton";
import ProductAddToListLink, { ProductAddToListLinkStyles } from "@eisinc/Overrides/Components/ProductAddToListLink";
import ProductImageBadges, { ProductImageBadgesStyles } from "@eisinc/Overrides/Components/ProductImageBadges";
import { CarouselCardType } from "@eisinc/XDL/types";
import useOverlayBadge from "@insite/client-framework/Common/Hooks/useOverlayBadge";
import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import sendProductRecommendationTracking from "@insite/client-framework/Store/Components/ProductRecommendations/Handlers/SendProductRecommendationTracking";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice from "@insite/content-library/Components/ProductPrice";
import { ProductQuantityBreakPricingStyles } from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import Badge from "@insite/mobius/Badge";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    productInfo: getProductInfoFromList(state, ownProps.carouselId, ownProps.product.id),
    // XNG-Changes
    isAuthenticated: getSession(state).isAuthenticated,
});

const mapDispatchToProps = {
    updateProductInfo,
    sendProductRecommendationTracking,
};

interface OwnProps {
    carouselId: string;
    product: ProductModel;
    recommendationId?: number;
    showImage: boolean;
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showPrice: boolean;
    showAddToCart: boolean;
    showAddToList: boolean;
    showImageBadges?: boolean;
    maximumNumberOfImageBadges?: number;
    showTextBadges?: boolean;
    maximumNumberOfTextBadges?: number;
    extendedStyles?: ProductCarouselProductStyles;
    // XNG-Changes
    actionsTemplate?: (product: ProductModel) => React.ReactNode;
    view?: CarouselCardType;
    showAvailability?: boolean;
    showCustomBadges?: boolean;
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ProductCarouselProductStyles {
    productImageStyles?: ProductImageStyles;
    productBrandWrapper?: InjectableCss;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    productPriceWrapper?: InjectableCss;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    productQuantityBreakPricingStyles?: ProductQuantityBreakPricingStyles;
    addToCartContainer?: GridContainerProps;
    qtyGridItem?: GridItemProps;
    productQuantityOrderedStyles?: TextFieldProps;
    addToCartButtonGridItem?: GridItemProps;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    badgeAreaWrapper?: InjectableCss;
    badgeTextWrapper?: InjectableCss;
    badgeImageWrapper?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    badgeImageProps?: JSX.IntrinsicElements["img"];
    // XNG-changes
    productGridContainerStyles?: ProductGridContainerStyle;
    productImageBadges?: ProductImageBadgesStyles;
    productActions?: ProductActionsStyle;
    thumbnailProductDescriptionStyles?: ProductDescriptionStyles;
    wrapper?: InjectableCss;
    actionsTemplateWrapper?: InjectableCss;
    badgesContainerStyle?: InjectableCss;
    partNumbersAndBrandNameWrapper?: InjectableCss;
    miniCardProductDescriptionStyles?: ProductDescriptionStyles;
    triggererWrapper?: InjectableCss;
}

export const productCarouselProductStyles: ProductCarouselProductStyles = {
    productBrandWrapper: {
        css: css`
            margin: 6px 0;
            min-height: 22px;
        `,
    },
    productDescriptionStyles: {
        productDetailLink: {
            css: css`
                margin-bottom: 6px;
            `,
        },
    },
    productPartNumbersStyles: {
        container: {
            css: css`
                flex-grow: 0;
                margin-bottom: 6px;
            `,
        },
        erpNumberLabelText: { size: 13 },
        erpNumberValueText: { size: 13 },
    },
    productPriceWrapper: {
        css: css`
            flex-grow: 1;
            margin-bottom: 6px;
        `,
    },
    productUnitOfMeasureSelectStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 6px;
            `,
        },
    },
    addToCartContainer: {
        gap: 10,
        css: css`
            flex-grow: 0;
            margin-bottom: 6px;
        `,
    },
    qtyGridItem: { width: 3 },
    productQuantityOrderedStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 0;
            `,
        },
    },
    addToCartButtonGridItem: { width: 9 },
    productAddToCartButtonStyles: {
        css: css`
            width: 100%;
        `,
    },
    productAddToListLinkStyles: {
        link: {
            typographyProps: { size: 13 },
            css: css`
                margin-bottom: 12px;
            `,
        },
    },
    badgeAreaWrapper: {
        css: css`
            position: relative;
        `,
    },
    badgeTextWrapper: {
        css: css`
            position: absolute;
            width: 50%;
            white-space: nowrap;
            overflow: hidden;
        `,
    },
    badgeImageWrapper: {
        css: css`
            position: absolute;
            width: 25%;
            height: 25%;
        `,
    },
    badgeText: {
        weight: "bold",
        css: css`
            width: 100%;
            margin: auto;
            ${wrapInContainerStyles}
        `,
    },
};

const ProductCarouselProduct = ({
    carouselId,
    product,
    showImage,
    showBrand,
    showTitle,
    showPartNumbers,
    showPrice,
    showAddToCart,
    showAddToList,
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
    updateProductInfo,
    extendedStyles,
    productInfo,
    recommendationId,
    sendProductRecommendationTracking,
    // XNG-Changes
    actionsTemplate,
    view,
    showAvailability,
    showCustomBadges = true,
    isAuthenticated,
}: Props) => {
    if (!product || !productInfo) {
        return null;
    }

    const [styles, setStyles] = useState(() => mergeToNew(productCarouselProductStyles, extendedStyles));

    // XNG-Changes: updating styles upon view change
    useEffect(() => {
        setStyles(() => mergeToNew(productCarouselProductStyles, extendedStyles));
    }, [view, extendedStyles]);

    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: carouselId, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: carouselId, productId: product.id, unitOfMeasure });
    };

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    const trackProductRecommendation = (isNewPageWasOpen: boolean) => {
        if (recommendationId) {
            sendProductRecommendationTracking({ recommendationId, isNewPageWasOpen });
        }
    };

    const {
        showOverlayBadge,
        overlayBadge,
        overlayBadgeWrapperStyles,
        badgeAreaWrapperRef,
        overlayBadgeWrapperClickHandler,
    } = useOverlayBadge({
        productContext,
        showTextBadge: showTextBadges,
        showImageBadge: showImageBadges,
        styles,
    });

    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });

    // <XNG-Changes>
    const badges =
        (showCustomBadges && product.properties?.["badges"] && JSON.parse(product.properties["badges"])) || [];

    const customBadges = badges.length > 0 && (
        <StyledWrapper {...styles.badgesContainerStyle} {...{ view }}>
            <ProductImagesBadges product={product} badges={badges} />
        </StyledWrapper>
    );
    // </XNG-Changes>

    // XNG-Changes : change structure
    return (
        <ProductContext.Provider value={productContext} data-test-selector="widget_PurchasedProducts">
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.actionsTemplateWrapper}>
                    {actionsTemplate ? actionsTemplate(productContext.product) : null}
                </StyledWrapper>

                <ProductGridContainer extendedStyles={styles.productGridContainerStyles}>
                    <ProductGridContainer.Image>
                        {showImage && !showOverlayBadge && (
                            <ProductImage
                                {...{ widgetId: carouselId }}
                                product={productContext}
                                onClick={() => trackProductRecommendation(true)}
                                extendedStyles={styles.productImageStyles}
                            />
                        )}
                        <StyledWrapper
                            {...styles.badgeAreaWrapper}
                            ref={badgeAreaWrapperRef}
                            data-test-selector="productCarouselBadge"
                            {...{ showOverlayBadge }}
                        >
                            {showImage && showOverlayBadge && overlayBadge && (
                                <>
                                    <ProductImage
                                        {...{ widgetId: carouselId }}
                                        extendedStyles={styles.productImageStyles}
                                        product={productContext}
                                    />
                                    <StyledWrapper
                                        {...overlayBadgeWrapperStyles}
                                        onClick={overlayBadgeWrapperClickHandler}
                                    >
                                        <Badge
                                            type={overlayBadge.badgeType}
                                            value={
                                                overlayBadge.badgeType === "Image"
                                                    ? overlayBadge.largeImageBadgePath
                                                    : overlayBadge.displayText
                                            }
                                            badgeTextColor={overlayBadge.textColorHexCode}
                                            badgeBackgroundColor={overlayBadge.badgeColorHexCode}
                                            badgeTextRounding={overlayBadge.badgeStyle === "Round" ? 20 : 0}
                                            imageAltText={
                                                overlayBadge.imageAltText
                                                    ? overlayBadge.imageAltText
                                                    : overlayBadge.name
                                            }
                                            textBadgeCss={styles.badgeText}
                                            imageBadgeProps={styles.badgeImageProps}
                                        />
                                    </StyledWrapper>
                                </>
                            )}
                        </StyledWrapper>
                        {view !== "miniCard" && customBadges}
                    </ProductGridContainer.Image>

                    <ProductGridContainer.Info>
                        {showTitle && (
                            <ProductDescription
                                {...{ widgetId: carouselId }}
                                product={productContext}
                                onClick={() => {
                                    trackProductRecommendation(true);
                                }}
                                extendedStyles={
                                    view === "condensed"
                                        ? styles.thumbnailProductDescriptionStyles
                                        : view === "miniCard"
                                          ? styles.miniCardProductDescriptionStyles
                                          : styles.productDescriptionStyles
                                }
                            />
                        )}
                        <StyledWrapper {...styles.partNumbersAndBrandNameWrapper}>
                            {showPartNumbers && (
                                <ProductPartNumbers
                                    productNumber={productContext.product.productNumber}
                                    showProductNumber={true}
                                    customerProductNumber={productContext.product.customerProductNumber}
                                    manufacturerItem={productContext.product.manufacturerItem}
                                    showLabel={true}
                                />
                            )}
                            {showBrand && productContext.product.brand && (
                                <ProductBrand
                                    brand={productContext.product.brand}
                                    extendedStyles={styles.productBrandStyles}
                                    {...{ showLabel: true }}
                                />
                            )}
                        </StyledWrapper>
                        {view === "miniCard" && customBadges}

                        {showTextBadges && textBadges && (
                            <ProductTextBadges badgeList={textBadges} maxBadges={maximumNumberOfTextBadges ?? 4} />
                        )}
                    </ProductGridContainer.Info>

                    <ProductGridContainer.Price>
                        {showPrice && productContext.product?.isVariantParent && <VariantParentProductPrice />}
                        {showPrice && !productContext.product?.isVariantParent && (
                            <ProductPrice
                                product={productContext}
                                showLabel={false}
                                showSavings={true}
                                showInvalidPriceMessage={true}
                            />
                        )}
                    </ProductGridContainer.Price>

                    <ProductGridContainer.AddToCartActions>
                        {showAddToCart && (
                            <ProductActions
                                extendedStyles={{
                                    ...styles.productActions,
                                    wrapper: {
                                        ...styles.productActions?.wrapper,
                                        css: css`
                                            ${styles.productActions?.wrapper?.css}
                                            ${view === "condensed"
                                                ? css`
                                                      gap: 6px;
                                                  `
                                                : css`
                                                      gap: 8px;
                                                  `}
                                        `,
                                    },
                                }}
                                hideUOM={view === "condensed"}
                                onAddToCartClick={() => {
                                    trackProductRecommendation(true);
                                }}
                            />
                        )}
                    </ProductGridContainer.AddToCartActions>

                    <ProductGridContainer.Availability>
                        {showAvailability && <ProductContextAvailability />}
                        {showImageBadges && (
                            <ProductImageBadges
                                badgeList={imageBadges || []}
                                maxBadges={maximumNumberOfImageBadges ?? 4}
                            />
                        )}
                        {showTextBadges && textBadges && (
                            <ProductTextBadges badgeList={textBadges} maxBadges={maximumNumberOfTextBadges ?? 4} />
                        )}
                    </ProductGridContainer.Availability>

                    <ProductGridContainer.AddToList>
                        {showAddToList && (
                            <ProductAddToListLink
                                data-test-selector="addToListLink"
                                showIcon={true}
                                extendedStyles={styles.productAddToListLinkStyles}
                            />
                        )}
                        {showAddToList && !product.canAddToWishlist && isAuthenticated && (
                            <Skeleton height="14px" disabledAnimation width="100%" />
                        )}
                    </ProductGridContainer.AddToList>
                </ProductGridContainer>
            </StyledWrapper>
        </ProductContext.Provider>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductCarouselProduct);
